// Legacy: portfoliosettings.component.ts smartTailorRun + auto_format_check query handling

import type { ResumeTargetFormat } from '@/features/resume/types';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/lib/storage/web-storage';

export const SMART_TAILOR_PENDING_KEYS = {
  runId: 'smart_tailor_run_id_pending',
  mode: 'smart_tailor_mode_pending',
  legacyRunId: 'smart_tailor_run_id',
  legacyResumeId: 'smart_tailor_resume_id',
} as const;

/** Strict format parse — returns null for missing/invalid (Angular normalizeTargetFormat). */
export function parseStrictTargetFormat(
  value: string | null | undefined,
): ResumeTargetFormat | null {
  if (value == null || value === '') {
    return null;
  }
  const normalized = value.toLowerCase().trim();
  if (
    normalized === 'universal' ||
    normalized === 'europass' ||
    normalized === 'uae' ||
    normalized === 'india' ||
    normalized === 'usa' ||
    normalized === 'germany'
  ) {
    return normalized;
  }
  return null;
}

export function parseSmartTailorRunId(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function persistSmartTailorPending(runId: number): void {
  setLocalStorageItem(SMART_TAILOR_PENDING_KEYS.runId, String(runId));
  setLocalStorageItem(SMART_TAILOR_PENDING_KEYS.mode, 'preview');
}

export function clearSmartTailorPending(): void {
  removeLocalStorageItem(SMART_TAILOR_PENDING_KEYS.runId);
  removeLocalStorageItem(SMART_TAILOR_PENDING_KEYS.mode);
  removeLocalStorageItem(SMART_TAILOR_PENDING_KEYS.legacyRunId);
  removeLocalStorageItem(SMART_TAILOR_PENDING_KEYS.legacyResumeId);
}

export function readSmartTailorPendingRunId(): number | null {
  const pending = getLocalStorageItem(SMART_TAILOR_PENDING_KEYS.runId);
  const mode = getLocalStorageItem(SMART_TAILOR_PENDING_KEYS.mode);
  if (mode === 'preview') {
    return parseSmartTailorRunId(pending);
  }
  return parseSmartTailorRunId(getLocalStorageItem(SMART_TAILOR_PENDING_KEYS.legacyRunId));
}

export function shouldTriggerAutoFormatCheck(
  autoFormatCheck: string | null,
  hasResumeId: boolean,
): boolean {
  return autoFormatCheck === 'true' && hasResumeId;
}

export function stripActionFromSearch(search: string): URLSearchParams {
  const params = new URLSearchParams(search);
  params.delete('action');
  return params;
}

export function portfolioSettingsPathFromParams(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `/account/portfoliosettings?${query}` : '/account/portfoliosettings';
}

/** Sync history update so stale router.replace cannot re-insert action=import. */
export function replacePortfolioSettingsUrlSync(url: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.history.replaceState(window.history.state, '', url);
}

export function buildPortfolioSettingsModeUrl(options: {
  search: string;
  resumeId: number;
  mode: 'preview' | 'editor';
  extraParams?: Record<string, string | null>;
  stripAction?: boolean;
}): string {
  const params = new URLSearchParams(options.search);
  params.set('mode', options.mode);
  params.set('resumeId', String(options.resumeId));
  if (options.extraParams) {
    Object.entries(options.extraParams).forEach(([key, value]) => {
      if (value == null || value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
  }
  if (options.stripAction) {
    params.delete('action');
  }
  return portfolioSettingsPathFromParams(params);
}
